/**
 * MetaLeadAds — Iter Feb 2026 R40.
 *
 * Meta Lead Ads dashboard: connected Pages → Lead Gen forms → captured
 * leads. Users can:
 *   • Refresh Pages / Forms from Meta (or from demo data)
 *   • Subscribe/unsubscribe the leadgen webhook per Page
 *   • Attach a Brand + Client to a Page (auto-tag leads)
 *   • Edit the Meta-field → Acquilix-field mapping per form
 *   • Backfill last N days of leads
 *   • View the recent leads captured for each form
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Facebook, RefreshCw, Bell, BellOff, Users, FileText, Download, Settings2,
  Trash2, MapPin, Cog, ExternalLink, CheckCircle2,
} from "lucide-react";

const ACQUILIX_FIELDS = [
  { key: "name",     label: "Name" },
  { key: "email",    label: "Email" },
  { key: "phone",    label: "Phone" },
  { key: "company",  label: "Company" },
  { key: "city",     label: "City" },
  { key: "country",  label: "Country" },
  { key: "job_title", label: "Job title" },
  { key: "notes",    label: "Notes (append)" },
  { key: "custom.budget",   label: "Custom · Budget" },
  { key: "custom.timeline", label: "Custom · Timeline" },
  { key: "custom.other",    label: "Custom · Other" },
];

export default function MetaLeadAds() {
  const { activeWorkspaceId } = useAuth();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [forms, setForms] = useState([]);
  const [brands, setBrands] = useState([]);
  const [clients, setClients] = useState([]);
  const [mappingForm, setMappingForm] = useState(null); // full form doc for the dialog

  const loadPages = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/perf/meta/pages`);
      setPages(data?.rows || []);
      if (!selectedPage && data?.rows?.length) setSelectedPage(data.rows[0]);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load Pages")); }
    finally { setLoading(false); }
  }, [activeWorkspaceId, selectedPage]);

  const loadForms = useCallback(async (page) => {
    if (!page) { setForms([]); return; }
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/perf/meta/pages/${page.id}/lead-forms`);
      setForms(data?.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load forms")); }
  }, [activeWorkspaceId]);

  useEffect(() => { loadPages(); }, [loadPages]);
  useEffect(() => { loadForms(selectedPage); }, [selectedPage, loadForms]);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/brands`).then(r => setBrands(r.data?.rows || r.data || [])).catch(() => {});
    api.get(`/workspaces/${activeWorkspaceId}/clients`).then(r => setClients(r.data?.rows || r.data || [])).catch(() => {});
  }, [activeWorkspaceId]);

  const refreshPages = async () => {
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/perf/meta/pages/refresh`);
      toast.success(`${data.imported} Pages synced`);
      loadPages();
    } catch (e) { toast.error(apiErrorMessage(e, "Refresh Pages failed")); }
  };

  const refreshForms = async () => {
    if (!selectedPage) return;
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/perf/meta/pages/${selectedPage.id}/lead-forms/refresh`);
      toast.success(`${data.imported} Lead-Gen forms synced`);
      loadForms(selectedPage);
    } catch (e) { toast.error(apiErrorMessage(e, "Refresh forms failed")); }
  };

  const toggleSubscribe = async (page) => {
    try {
      if (page.subscribed) {
        await api.delete(`/workspaces/${activeWorkspaceId}/perf/meta/pages/${page.id}/subscribe`);
        toast.success("Unsubscribed from leadgen");
      } else {
        await api.post(`/workspaces/${activeWorkspaceId}/perf/meta/pages/${page.id}/subscribe`);
        toast.success("Subscribed — realtime lead push is now ON");
      }
      loadPages();
    } catch (e) { toast.error(apiErrorMessage(e, "Toggle failed")); }
  };

  const attachPageBrand = async (page, brand_id) => {
    try {
      await api.patch(`/workspaces/${activeWorkspaceId}/perf/meta/pages/${page.id}`, { brand_id });
      toast.success("Brand attached");
      loadPages();
    } catch (e) { toast.error(apiErrorMessage(e, "Attach failed")); }
  };

  const openMapping = async (form) => {
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/perf/meta/lead-forms/${form.id}`);
      setMappingForm(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Load form failed")); }
  };

  const backfill = async (form) => {
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/perf/meta/lead-forms/${form.id}/backfill?days=90`);
      toast.success(`Imported ${data.created} · updated ${data.updated} leads`);
      loadForms(selectedPage);
    } catch (e) { toast.error(apiErrorMessage(e, "Backfill failed")); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="meta-lead-ads-page">
      <PageHeader
        title="Meta Lead Ads"
        eyebrow="Performance"
        description="Realtime Lead-Ads pipeline from Facebook / Instagram Pages into your CRM. Subscribe once, map fields once, then every submission lands as a tagged lead."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/app/performance/meta-setup">
              <Button variant="outline" className="rounded-none h-9" data-testid="meta-leads-open-wizard">
                <Cog className="w-4 h-4 mr-1.5" /> Setup wizard
              </Button>
            </Link>
            <Button className="rounded-none h-9" onClick={refreshPages} data-testid="meta-leads-refresh-pages">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh Pages
            </Button>
          </div>
        }
      />

      <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
        {/* Left: Pages list */}
        <div className="w-full md:w-80 border-r overflow-auto">
          <div className="px-3 py-2 border-b text-[10px] uppercase tracking-widest text-muted-foreground bg-neutral-50">Connected Pages</div>
          {loading && <div className="p-3 text-sm text-muted-foreground">Loading…</div>}
          {!loading && pages.length === 0 && (
            <div className="p-4 text-center text-sm text-muted-foreground space-y-2">
              <Facebook className="w-8 h-8 mx-auto text-neutral-400" />
              <div>No Pages yet.</div>
              <div className="text-xs">Create a Meta connection under Performance → Accounts (mode=demo works for testing), then click &ldquo;Refresh Pages&rdquo;.</div>
            </div>
          )}
          {pages.map(p => (
            <button
              key={p.id}
              onClick={() => setSelectedPage(p)}
              className={`w-full text-left p-3 border-b hover:bg-neutral-50 transition-colors ${selectedPage?.id === p.id ? "bg-neutral-100" : ""}`}
              data-testid={`meta-page-${p.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{p.name}</div>
                  <div className="text-[10px] text-muted-foreground">{p.category} · {p.external_id}</div>
                </div>
                {p.subscribed
                  ? <span className="text-[9px] px-1.5 py-0.5 bg-emerald-100 border border-emerald-400 text-emerald-800 uppercase tracking-widest">Live</span>
                  : <span className="text-[9px] px-1.5 py-0.5 bg-neutral-100 border text-neutral-500 uppercase tracking-widest">Off</span>}
              </div>
              {p.brand_id && brands.length > 0 && (
                <div className="text-[10px] text-muted-foreground mt-1">
                  Brand: {(brands.find(b => b.id === p.brand_id)?.name) || "—"}
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Right: Selected page + forms */}
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {selectedPage ? (
            <>
              {/* Page header card */}
              <div className="border p-3 flex items-center gap-3 flex-wrap" data-testid="meta-page-header">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{selectedPage.name}</div>
                  <div className="text-xs text-muted-foreground">Page ID {selectedPage.external_id} · {selectedPage.category}</div>
                </div>
                <Select value={selectedPage.brand_id || "none"} onValueChange={v => attachPageBrand(selectedPage, v === "none" ? null : v)}>
                  <SelectTrigger className="h-9 w-52 rounded-none text-xs" data-testid="meta-page-brand-select">
                    <SelectValue placeholder="Attach brand…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— no brand —</SelectItem>
                    {brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button
                  variant={selectedPage.subscribed ? "outline" : "default"}
                  className="rounded-none h-9"
                  onClick={() => toggleSubscribe(selectedPage)}
                  data-testid="meta-page-toggle-subscribe"
                >
                  {selectedPage.subscribed
                    ? <><BellOff className="w-4 h-4 mr-1.5" /> Unsubscribe</>
                    : <><Bell className="w-4 h-4 mr-1.5" /> Subscribe · leadgen</>}
                </Button>
                <Button variant="outline" className="rounded-none h-9" onClick={refreshForms} data-testid="meta-page-refresh-forms">
                  <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh forms
                </Button>
              </div>

              {/* Forms grid */}
              {forms.length === 0 && (
                <div className="border-2 border-dashed p-8 text-center space-y-2">
                  <FileText className="w-8 h-8 mx-auto text-neutral-400" />
                  <div className="font-medium">No Lead-Gen forms yet</div>
                  <div className="text-sm text-muted-foreground">Click &ldquo;Refresh forms&rdquo; to pull them from Meta.</div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {forms.map(f => (
                  <FormCard
                    key={f.id}
                    form={f}
                    onEdit={() => openMapping(f)}
                    onBackfill={() => backfill(f)}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="text-sm text-muted-foreground p-6">Pick a Page from the left to see its Lead-Gen forms.</div>
          )}
        </div>
      </div>

      {mappingForm && (
        <MappingDialog
          form={mappingForm}
          brands={brands}
          clients={clients}
          onClose={() => setMappingForm(null)}
          onSaved={() => { setMappingForm(null); loadForms(selectedPage); }}
          wsId={activeWorkspaceId}
        />
      )}
    </div>
  );
}

function FormCard({ form, onEdit, onBackfill }) {
  return (
    <div className="border p-3 space-y-2" data-testid={`meta-form-card-${form.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm truncate">{form.name}</div>
          <div className="text-[10px] text-muted-foreground">Form ID {form.external_id} · {form.locale}</div>
        </div>
        <span className={`text-[9px] px-1.5 py-0.5 uppercase tracking-widest ${form.status === "ACTIVE" ? "bg-emerald-100 border border-emerald-400 text-emerald-800" : "bg-neutral-100 border text-neutral-500"}`}>{form.status}</span>
      </div>
      <div className="text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {form.leads_count || 0} leads</span>
        {form.last_backfill_at && <span className="ml-3">Last sync: {form.last_backfill_at.slice(0, 16).replace("T", " ")}</span>}
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Button size="sm" variant="outline" className="rounded-none h-8 flex-1" onClick={onEdit} data-testid={`meta-form-map-${form.id}`}>
          <Settings2 className="w-3.5 h-3.5 mr-1" /> Field mapping
        </Button>
        <Button size="sm" className="rounded-none h-8 flex-1" onClick={onBackfill} data-testid={`meta-form-backfill-${form.id}`}>
          <Download className="w-3.5 h-3.5 mr-1" /> Backfill 90d
        </Button>
      </div>
    </div>
  );
}

// ─────────────── Field mapping dialog ───────────────

function MappingDialog({ form, brands, clients, onClose, onSaved, wsId }) {
  const [mapping, setMapping] = useState(form.mapping || {});
  const [brandId, setBrandId] = useState(form.brand_id || "");
  const [clientId, setClientId] = useState(form.client_id || "");
  const [stage, setStage] = useState(form.lead_stage_default || "new");
  const [busy, setBusy] = useState(false);

  const questions = form.questions || [];

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/perf/meta/lead-forms/${form.id}/mapping`, {
        mapping,
        brand_id: brandId || null,
        client_id: clientId || null,
        lead_stage_default: stage,
      });
      toast.success("Mapping saved — new submissions will land as tagged leads");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-none">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings2 className="w-4 h-4" /> Field mapping · {form.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Attach Brand (auto-tag leads)</Label>
              <Select value={brandId || "none"} onValueChange={v => setBrandId(v === "none" ? "" : v)}>
                <SelectTrigger className="h-9 rounded-none" data-testid="meta-map-brand"><SelectValue placeholder="— none —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— none —</SelectItem>
                  {brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Attach Client</Label>
              <Select value={clientId || "none"} onValueChange={v => setClientId(v === "none" ? "" : v)}>
                <SelectTrigger className="h-9 rounded-none" data-testid="meta-map-client"><SelectValue placeholder="— none —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— none —</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Default lead stage</Label>
            <Input value={stage} onChange={e => setStage(e.target.value)} className="h-9 rounded-none" placeholder="new" data-testid="meta-map-stage" />
          </div>

          <div>
            <div className="text-xs font-semibold mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Map each Meta question → Acquilix lead field</div>
            <div className="border">
              <div className="grid grid-cols-2 gap-0 text-[10px] uppercase tracking-widest bg-neutral-100 px-2 py-1">
                <div>Meta question</div>
                <div>Acquilix field</div>
              </div>
              {questions.length === 0 && <div className="p-3 text-sm text-muted-foreground">This form has no questions to map.</div>}
              {questions.map(q => {
                const key = q.key || q.name;
                return (
                  <div key={key} className="grid grid-cols-2 gap-0 border-t items-center px-2 py-1.5">
                    <div className="text-xs">
                      <div className="font-medium">{q.label || key}</div>
                      <code className="text-[10px] text-muted-foreground">{key} · {q.type || "STRING"}</code>
                    </div>
                    <Select value={mapping[key] || "notes"} onValueChange={v => setMapping({ ...mapping, [key]: v })}>
                      <SelectTrigger className="h-8 rounded-none text-xs" data-testid={`meta-map-row-${key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ACQUILIX_FIELDS.map(f => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                        <SelectItem value={`custom.${key}`}>Custom · {key}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="meta-map-save">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> {busy ? "Saving…" : "Save mapping"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
